// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { memo, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { equals } from "ramda"
import { useMeasure, useUpdateEffect } from "react-use"
import styled, { css, SimpleInterpolation } from "styled-components/macro"
import { darken } from "polished"
import { useDispatch, useSelector } from "react-redux"
import { t } from "@lingui/macro"
import { DragPreview, useDrag } from "react-aria"
import RowDuration from "../shared/RowDuration"
import TimeRowIconDesc from "../shared/TimeRowIconDesc"
import UntrashButton, { WrapperUntrash } from "../shared/UntrashButton"
import TimeBlockRowActions from "./TimeBlockRowActions"
import TagList from "../shared/TagList"
import { SharedTimeRow } from "features/shared/styles"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import * as TrashedTimeRowSlice from "../trash/slice"
import * as UserContextSlice from "../../user-context/slice"
import {
  DROPDOWN_PARENT_ACTIVE_CLASSNAME,
  DROPDOWN_PARENT_CLASSNAME,
  GROUPS_CONTENT_CLASSNAME,
  ROW_ACTIONS_CLASSNAME,
  ROW_COPY_TO_CLIPBOARD_CLASSNAME,
  ROW_DURATION_VISIBILITY_CLASSNAME,
  TIME_ROW_CLASSNAME,
  TIME_ROW_STATE_FAILED,
} from "../../shared/classNames"
import RemoveFromGroupButton, { Wrapper as RemoveFromGroupButtonWrapper } from "./RemoveFromGroupButton"
import { ThemeType } from "../../../styled/styles"
import RowState from "../shared/RowState"
import { isTouchDevice } from "../../../lib/utils"
import { draggableListKey } from "../../../app/dnd"
import { ActivityTypeRef } from "generated/wisetime/user/type/activity_type_ref_pb"
import { TagRef } from "generated/wisetime/user/type/tag_ref_pb"
import { PostStatus, TagSuggestion, TrashStatus } from "generated/wisetime/user/timeline/v4/timeline_pb"
import { TagCategoryRef } from "../../../generated/wisetime/user/type/tag_category_ref_pb"
import { HexColor } from "../color"

const getBackgroundColor = (theme: ThemeType, isActive: boolean, timeGroupPostStatus?: PostStatus) => {
  if (!isActive && timeGroupPostStatus === PostStatus.FAILED) {
    return theme.colors.wispPink
  }
  if (isActive) {
    return darken(0.02, theme.colors.selago)
  }
  return theme.colors.white
}

const Row = styled(SharedTimeRow)<{
  isActive: boolean,
  timeGroupPostStatus?: PostStatus,
  isGroup: boolean,
  isInDragState?: boolean,
  hourDividerColor?: HexColor,
  updatingStatus?: TrashStatus,
}>`
  cursor: ${({ isGroup }): string => isGroup ? "default" : "pointer"};
  border-width: ${({ isGroup }): string => isGroup ? "1px 1px 1px 0" : "1px"};
  border-color: ${({ isActive, theme }): string => isActive ? theme.colors.lavender : theme.colors.mercury};
  padding-left: 9px;
  
  ${({ hourDividerColor }) => hourDividerColor && css`
    &:after {
      content: "";
      width: 10px;
      height: 3px;
      background-color: ${hourDividerColor};
      position: absolute;
      left: -3px;
      bottom: -2px;
      z-index: 1;
    }
  `};

  &.draggable-preview {
    border-radius: 0;
  }

  ${`.${ROW_COPY_TO_CLIPBOARD_CLASSNAME}`} {
      display: none;
  }

  &.draggable-preview--multi-drag {
    &:after {
      content: "";
      width: 100%;
      height: 100%;
      border-width: 1px;
      border-color: ${({ isActive, theme }): string => isActive ? theme.colors.lavender : theme.colors.mercury};
      background-color: ${({ isActive, theme }): string => isActive ? darken(0.02, theme.colors.selago) : theme.colors.white};
      display: inline-block;
      position: absolute;
      top: 5px;
      left: 5px;
      z-index: -1;
    }
  }

  &.is-dragging {
    opacity: .5;
  }
  
  ${({ isInDragState }) => isInDragState && css`
    pointer-events: none;
  `}

  ${({
    isActive,
    timeGroupPostStatus,
    updatingStatus,
    theme,
  }): SimpleInterpolation => {
    if (isActive && updatingStatus === TrashStatus.TRASHED) {
      return css`
        border-color: ${theme.colors.mercury} transparent ${theme.colors.mercury} ${theme.colors.lightCoral};
        background-color: ${theme.colors.yourPink};
      `
    }
    if (updatingStatus === TrashStatus.TRASHED) {
      return css`
        background-color: ${theme.colors.wispPink};
      `
    }
    if (!isActive && (timeGroupPostStatus === PostStatus.SUCCESS || timeGroupPostStatus === PostStatus.PENDING)) {
      return css`
        background-color: ${theme.colors.alabaster};
      `
    }
    if (!isActive && (timeGroupPostStatus === PostStatus.FAILED || timeGroupPostStatus === PostStatus.TRANSIENT_FAILURE)) {
      return css`
        background-color: ${theme.colors.wispPink};
        ${isActive && css`
          &:after {
            content: "";
            width: 100%;
            height: 1px;
            background-color: ${theme.colors.lavender};
            position: absolute;
            bottom: -1px;
            left: 0;
            z-index: 1;
          }
        `}
      `
    }
    if (isActive) {
      return css`
        background-color: ${darken(0.02, theme.colors.selago)};

        &:after {
          content: "";
          width: 100%;
          height: 1px;
          background-color: ${theme.colors.lavender};
          position: absolute;
          bottom: -1px;
          left: 0;
          z-index: 1;
        }
      `
    }
    return css`
      background-color: ${theme.colors.white};
    `
  }};
  
  &:not(:first-child) {
    margin-top: -1px;
  }

  &:last-child {
    border-radius: 0 0 2px 2px;
  }

  &:hover {
    ${RemoveFromGroupButtonWrapper} {
      display: flex;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${({ theme, isActive, timeGroupPostStatus }): string => darken(0.04, getBackgroundColor(theme, isActive, timeGroupPostStatus))};

      > [data-tooltip] {
        transition-delay: .35s;
        opacity: 1;
        z-index: 101;
      }

      ${`.${ROW_COPY_TO_CLIPBOARD_CLASSNAME}`} {
          display: inline-flex;
      }
    }
  }
  
  @media (min-width: ${({ theme }): string => theme.screen.md}) and (hover: hover) and (pointer: fine) {
    &:hover {
      ${WrapperUntrash} {
        display: flex;
      }
    }

    &:hover,
    &.${DROPDOWN_PARENT_ACTIVE_CLASSNAME} {
      .${ROW_ACTIONS_CLASSNAME} {
        opacity: 1;
      }
      
      .${TIME_ROW_STATE_FAILED},
      .${ROW_DURATION_VISIBILITY_CLASSNAME} {
        display: none;
      }
    }
  }

  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    flex-wrap: wrap;
    padding-left: 14px;
  }
`

const TimeLabel = styled.span`
  color: ${({ theme }) => theme.colors.nero};
  font-size: 1.1rem;
  min-width: 5rem;
  padding: 1rem 0 1rem 1.6rem;
`

const DraggedRowsCount = styled.span`
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.indigo};
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  position: absolute;
  top: -8px;
  left: -8px;
  border-radius: 4px;
  padding: 0 5px;
`

type Props = {
  timeRowId: string,
  timeGroupId: string,
  tagsList: TagRef.AsObject[],
  tagSuggestionsList: TagSuggestion.AsObject[],
  activityTypesList: ActivityTypeRef.AsObject[],
  tagCategoriesList: TagCategoryRef.AsObject[],
  activity: string,
  description: string,
  iconUrl: string,
  duration?: number,
  trashStatus: TrashStatus,
  timeGroupPostStatus?: PostStatus,
  isActive?: boolean,
  formattedTime?: string,
  hourDividerColor?: HexColor,
  className?: string,
  // When the shift key is down, this will be used to determine the selection range
  rangeSelector?: (clickedRowId: string) => string[],
}

export const TimeBlockRow = ({
  timeRowId,
  timeGroupId,
  tagsList,
  tagSuggestionsList,
  activityTypesList,
  tagCategoriesList,
  activity,
  description,
  iconUrl,
  duration = 0,
  trashStatus,
  timeGroupPostStatus,
  isActive = false,
  formattedTime,
  hourDividerColor,
  className,
  rangeSelector,
}: Props): React.ReactElement<Props> => {
  const dispatch = useDispatch()
  const pendingStatus = useSelector(TimelineSlice.selectUpdatingTimeRowsStatus)[timeRowId]
  const hasTimeGroupsForDate = useSelector(TimelineSlice.selectHasTimeGroupsForDate)
  const draggingTimeRowId = useSelector(TimelineSlice.selectDraggingTimeRowId)
  const isInDragState = useSelector(TimelineSlice.selectIsInDragState)
  const activeTimeRowsIds = useSelector(TimelineSlice.selectActiveTimeRowsIds)
  const isActiveMultiDrag = isActive && (
    draggingTimeRowId ? [timeRowId, draggingTimeRowId].every(id => activeTimeRowsIds.includes(id)) : false
  )
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const isTrashed = trashStatus === TrashStatus.TRASHED
  const isGroup = timeGroupId !== ""
  const isInProgress = timeGroupPostStatus === PostStatus.PENDING || timeGroupPostStatus === PostStatus.TRANSIENT_FAILURE
  const isDisabled = timeGroupPostStatus === PostStatus.SUCCESS
    || pendingStatus === TrashStatus.TRASHED
  const isNotDisabledAndNotGroupAndNotTrashed = !isDisabled && !isInProgress && !isGroup && !isTrashed

  const preview = useRef(null)

  const { dragProps, isDragging } = useDrag({
    preview,
    getItems: () => [
      {
        [draggableListKey]: JSON.stringify(activeTimeRowsIds.includes(timeRowId) ? activeTimeRowsIds : [timeRowId])
      },
    ],
    onDragEnd: () => {
      dispatch(TimelineAdditionalUiSlice.setTimesheetIsPinnedByDrag(false))
    },
    isDisabled: isTrashed || isGroup || isTouchDevice,
  })
  const [ref, { width }] = useMeasure<HTMLLIElement>()

  useUpdateEffect(() => {
    dispatch(TimelineSlice.setDraggingTimeRowId(isDragging ? timeRowId : undefined))

    const scrollContainer = document.querySelector(`.${GROUPS_CONTENT_CLASSNAME}`)
    if (!scrollContainer || !isDragging) {
      return
    }

    const autoScroll = (e: { clientY: number }) => {
      const rect = scrollContainer.getBoundingClientRect()
      const top = e.clientY - rect.top
      if (top < 50) {
        scrollContainer.scrollTo(0, scrollContainer.scrollTop - 1)
      }
      const bottom = rect.bottom - e.clientY
      if (bottom < 50 && bottom > 0) {
        scrollContainer.scrollTo(0, scrollContainer.scrollTop + 1)
      }
    }

    window.addEventListener("dragover", autoScroll)

    return () => {
      window.removeEventListener("dragover", autoScroll)
    }
  }, [isDragging])

  const onClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>): void => {
    if (isDisabled) {
      return
    }

    if (event.shiftKey && rangeSelector !== undefined && !isActive) {
      const selectedRowsIds = rangeSelector(timeRowId)
      if (isTrashed) {
        dispatch(TrashedTimeRowSlice.addActiveTrashedTimeRowIds(selectedRowsIds))
      } else {
        dispatch(TimelineSlice.addActiveTimeRowIds({ timeRowIds: selectedRowsIds }))
      }
      return
    }
    if (isGroup) {
      return
    }

    if (isActive) {
      // Unselect the time row
      if (isTrashed) {
        dispatch(TrashedTimeRowSlice.removeActiveTrashedTimeRowIds([timeRowId]))
      } else {
        dispatch(TimelineSlice.removeActiveTimeRowIds({ timeRowIds: [timeRowId]}))
      }
    } else {
      if (isTrashed) {
        dispatch(TrashedTimeRowSlice.addActiveTrashedTimeRowIds([timeRowId]))
      } else {
        dispatch(TimelineSlice.addActiveTimeRowIds({ timeRowIds: [timeRowId]}))
      }
    }
  }

  const filteredTags = tagsList.filter(tag => tag.siteId === currentSiteId)
  const filteredTagSuggestions = tagSuggestionsList.filter(suggestion => suggestion?.tag?.siteId === currentSiteId)
  const filteredActivityTypes = activityTypesList.filter(activity => activity.siteId === currentSiteId)
  const filteredTagCategories = tagCategoriesList
    .filter(tagCategory => tagCategory.siteId === currentSiteId)
  const totalAttachments = filteredTags.length
    + filteredTagSuggestions.length
    + filteredActivityTypes.length
    + filteredTagCategories.length

  useEffect(() => {
    if (filteredActivityTypes.length > 1) {
      /*
       * Rare edge case which in theory shouldn't happen,
       * however, we need to show it as a warning in staging/production
       */
      console.warn(`timeRowId(${timeRowId}) contains ${filteredActivityTypes.length} activity types for siteId(${currentSiteId}), it should be one per site.`)
    }
  }, [filteredActivityTypes])

  const renderActions = (): React.ReactNode => {
    if (isNotDisabledAndNotGroupAndNotTrashed && !isDragging) {
      return (
        <TimeBlockRowActions
          timeRowId={timeRowId}
          tagsList={tagsList}
          activityTypesList={activityTypesList}
          hasTimeGroupsForDate={hasTimeGroupsForDate}
          isActive={isActive}
        />
      )
    }
    if (isGroup && !isDisabled && !isInProgress) {
      return (
        <RemoveFromGroupButton
          timeGroupId={timeGroupId}
          timeRowId={timeRowId}
        />
      )
    }
    return null
  }
  
  const rowClassNames = () => {
    let classNames = `${TIME_ROW_CLASSNAME} ${DROPDOWN_PARENT_CLASSNAME}`
    if (className) {
      classNames += ` ${className}`
    }
    if (isDragging || isActiveMultiDrag) {
      classNames += " is-dragging"
    }
    return classNames
  }

  return (
    <>
      <Row
        {...dragProps}
        ref={ref}
        onClick={onClick}
        className={rowClassNames()}
        onMouseDown={(event: React.MouseEvent<HTMLLIElement>): void => {
          if (event.shiftKey) {
            event.preventDefault()
          }
        }}
        isActive={isActive}
        isInDragState={isInDragState}
        timeGroupPostStatus={timeGroupPostStatus}
        updatingStatus={pendingStatus}
        isGroup={isGroup}
        hourDividerColor={hourDividerColor}
      >
        {formattedTime && (
          <TimeLabel title={t({ id: "label_local_time_of_activity", message: "Local time of activity" })}>
            {formattedTime}
          </TimeLabel>
        )}
        <TimeRowIconDesc
          activity={activity}
          description={description}
          iconUrl={iconUrl}
          isGroup={isGroup}
          timeGroupPostStatus={timeGroupPostStatus}
          pendingStatus={pendingStatus}
          isDragging={isDragging}
          showTooltip={!isTouchDevice && !isDragging}
        />
        {!isGroup && totalAttachments !== 0 && (
          <TagList
            timeGroupIdOrTimeRowsIds={[timeRowId]}
            tagWithOverlay={!isActiveMultiDrag}
            isDisabled={isDisabled || isInProgress}
            isDisabledGroup={isGroup}
            tagRefs={filteredTags}
            tagSuggestions={filteredTagSuggestions}
            activityTypeRefs={filteredActivityTypes}
            tagCategoriesList={filteredTagCategories}
          />
        )}
        {renderActions()}
        <RowDuration
          duration={duration}
          pendingStatus={pendingStatus}
          toggleVisibility={isNotDisabledAndNotGroupAndNotTrashed && !isDragging}
        />
        {trashStatus === TrashStatus.TRASHED && (
          <UntrashButton
            timeRowId={timeRowId}
            pendingStatus={pendingStatus}
            isActive={isActive}
          />
        )}
        <RowState
          timeRowId={timeRowId}
          trashStatus={trashStatus}
          pendingStatus={pendingStatus}
          isActive={isActive}
        />
      </Row>
      {createPortal(
        <DragPreview ref={preview}>
          {(items) => {
            const count = JSON.parse(items[0]?.[draggableListKey] ?? "[]")
            const isMultiDrag = count?.length > 1
            return (
              <Row
                style={{ width: width + "px" }}
                className={`draggable-preview ${isMultiDrag ? "draggable-preview--multi-drag" : ""}`}
                isActive={isActive}
                timeGroupPostStatus={timeGroupPostStatus}
                updatingStatus={pendingStatus}
                isGroup={isGroup}
              >
                <TimeRowIconDesc
                  activity={activity}
                  description={description}
                  iconUrl={iconUrl}
                  isGroup={isGroup}
                  timeGroupPostStatus={timeGroupPostStatus}
                  pendingStatus={pendingStatus}
                  showTooltip={false}
                />
                {!isGroup && totalAttachments !== 0 && (
                  <TagList
                    timeGroupIdOrTimeRowsIds={[timeRowId]}
                    tagWithOverlay
                    isDisabled={isDisabled || isInProgress}
                    isDisabledGroup={isGroup}
                    tagRefs={filteredTags}
                    tagSuggestions={filteredTagSuggestions}
                    activityTypeRefs={filteredActivityTypes}
                    tagCategoriesList={filteredTagCategories}
                  />
                )}
                {renderActions()}
                <RowDuration
                  duration={duration}
                  pendingStatus={pendingStatus}
                  toggleVisibility={isNotDisabledAndNotGroupAndNotTrashed && !isDragging}
                />
                {isMultiDrag && <DraggedRowsCount>{count.length}</DraggedRowsCount>}
              </Row>
            )
          }}
        </DragPreview>,
        document.body,
      )}
    </>
  )
}

export default memo(
  TimeBlockRow,
  (prevProps: Props, nextProps: Props) => equals(prevProps, nextProps),
)
